import { Box, Button, Typography } from '@mui/material';

import { useParams, useSearchParams } from 'src/routes/hooks';

import dayjs from 'src/utils/dayjs';
import { fDate, formatPatterns } from 'src/utils/format-time';

import { BackBtn } from 'src/components';

import { buildSupplierPdfData, DownloadTenderPDFButton } from 'src/components/TenderSubmissionPDF';

import { useGetSupplierContractApplication } from '../hooks';

const content: Record<
  string,
  {
    title: string;
    description: string | string[];
    infoBtn?: { title: string; icon: React.ReactNode };
  }
> = {
  introduction: {
    title: '{{contract.title}}',
    description:
      'In this section, you’ll review the key details about this contract, including the themes, activities, and geographical boundaries. Please confirm this information before proceeding with your application. Be aware this application includes 3 steps to submit.',
  },
  activities: {
    title: 'Select Activities and Commitments',
    description: [
      'These are the activities the procurer is interested in as part of delivering Social Value impact for this contract. ',
      'Choose which activities your organisation will commit to as part of the contract. For each selected activity, enter the units you can commit to and provide details on how you plan to deliver the activity.',
    ],
  },
  'qualitative-questions': {
    title: 'Contract Submission Questions',
    description:
      'As part of the submission process, please answer the following qualitative questions. These questions are designed to gather more detailed information about your approach to delivering the contract’s social value. Take your time to provide thoughtful answers.',
  },
};

const parseTemplate = (template: string = '', data: any) => {
  if (!data) return '';
  return template.replace(/{{(.*?)}}/g, (match, p1) => {
    const keys = p1.split('.');
    return keys.reduce((acc: any, key: string) => acc?.[key], data) || match;
  });
};

function SideBar() {
  const { id: applicationId } = useParams();
  const [searchParams] = useSearchParams();
  const currentStep = searchParams.get('step');
  const currentStepContent = content[currentStep as keyof typeof content] || {};
  const { data } = useGetSupplierContractApplication({
    applicationId: Number(applicationId),
    enabled: !!applicationId,
  });

  const pdfData = data ? buildSupplierPdfData(data) : null;

  return (
    <Box>
      {currentStep !== 'introduction' && <BackBtn />}
      <Typography variant="h3" mt={1.5}>
        {parseTemplate(currentStepContent.title, data)}
      </Typography>
      {Array.isArray(currentStepContent.description) ? (
        currentStepContent.description.map((desc, index) => (
          <Typography
            key={index}
            variant="body2"
            color="text.secondary"
            mt={index === 0 ? 1.5 : 0.75}
          >
            {desc}
          </Typography>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary" mt={1.5}>
          {currentStepContent.description}
        </Typography>
      )}
      {pdfData && (
        <Box mt={4}>
          <DownloadTenderPDFButton data={pdfData} />
        </Box>
      )}
      {data?.changeRequests && data.changeRequests.length > 0 && (
        <Box mt={3}>
          {data.changeRequests.map((changeRequest: any) => (
            <Box
              sx={{ backgroundColor: '#FFF0EC', px: '12px', py: '8px' }}
              key={changeRequest.id}
              mb={3}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#333',
                  fontSize: '14px',
                }}
              >
                Changes requested on {fDate(changeRequest.createdAt, formatPatterns.split.date)}
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  marginTop: '8px',
                  fontSize: '14px',
                }}
              >
                {changeRequest.feedback}
              </Typography>
              {changeRequest.changesDueAt && (
                <Box
                  sx={{
                    border: '2px solid #E8785A',
                    borderRadius: '8px',
                    padding: '16px',
                    marginTop: '12px',
                    backgroundColor: '#FFF0EC',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#E8785A',
                      fontWeight: 600,
                      fontSize: '14px',
                    }}
                  >
                    Deadline{' '}
                    {dayjs().isAfter(dayjs(changeRequest.changesDueAt)) ? 'Missed' : 'to resubmit'}:
                  </Typography>
                  <Typography
                    sx={{
                      color: '#E8785A',
                      fontWeight: 600,
                      fontSize: '14px',
                    }}
                  >
                    {fDate(changeRequest.changesDueAt, formatPatterns.split.dateTime)}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
      {currentStepContent?.infoBtn && (
        <Box display="flex" gap={1} alignItems="center" mt={4}>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            size="medium"
            startIcon={currentStepContent.infoBtn.icon}
            sx={{
              backgroundColor: '#FDBE9A',
              color: '#5F2120',
            }}
          >
            {currentStepContent.infoBtn.title}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default SideBar;
